import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useApi, storageApiRef } from '@backstage/core-plugin-api';
import { UserProfile, DEFAULT_USER_PROFILE } from './types';

const STORAGE_BUCKET = 'user-profile';
const STORAGE_KEY = 'profile';

/** Result of a profile operation */
export interface ProfileOperationResult {
  success: boolean;
  error?: Error;
}

/**
 * Hook for managing user profile data for dashboard personalization
 */
export function useUserProfile() {
  const storageBase = useApi(storageApiRef);
  // forBucket() returns a new object every call; memoize to keep a stable
  // reference so the useEffect([storageApi]) below only runs once on mount.
  const storageApi = useMemo(
    () => storageBase.forBucket(STORAGE_BUCKET),
    [storageBase],
  );
  const [profile, setProfile] = useState<UserProfile>(DEFAULT_USER_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const initializedRef = useRef(false);

  // Load profile from storage on mount
  useEffect(() => {
    setLoading(true);
    setError(null);

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const snapshot = storageApi.snapshot<any>(STORAGE_KEY);

      if (snapshot.presence === 'present' && snapshot.value) {
        setProfile(snapshot.value as UserProfile);
      }

      if (snapshot.presence !== 'unknown') {
        if (!initializedRef.current) {
          initializedRef.current = true;
        }
        setLoading(false);
      }
    } catch (err) {
      const loadError =
        err instanceof Error ? err : new Error('Failed to load user profile');
      setError(loadError);
      setLoading(false);
    }

    // Subscribe to storage changes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const subscription = storageApi.observe$<any>(STORAGE_KEY).subscribe({
      next: snap => {
        try {
          if (snap.presence === 'unknown') {
            return;
          }
          if (snap.presence === 'present' && snap.value) {
            setProfile(snap.value as UserProfile);
          } else if (snap.presence === 'absent') {
            setProfile(DEFAULT_USER_PROFILE);
          }
          if (!initializedRef.current) {
            initializedRef.current = true;
          }
          setLoading(false);
          setError(null);
        } catch (err) {
          const parseError =
            err instanceof Error
              ? err
              : new Error('Failed to parse profile data');
          setError(parseError);
        }
      },
      error: err => {
        const subscriptionError =
          err instanceof Error ? err : new Error('Profile subscription failed');
        setError(subscriptionError);
        setLoading(false);
      },
    });

    return () => subscription.unsubscribe();
  }, [storageApi]);

  /** Clear error state */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /** Save the profile to storage */
  const saveProfile = useCallback(
    async (updatedProfile: UserProfile): Promise<ProfileOperationResult> => {
      setSaving(true);
      setError(null);
      try {
        const toSave: UserProfile = {
          ...updatedProfile,
          updatedAt: new Date().toISOString(),
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await storageApi.set(STORAGE_KEY, toSave as any);
        setProfile(toSave);
        return { success: true };
      } catch (err) {
        const saveError =
          err instanceof Error ? err : new Error('Failed to save profile');
        setError(saveError);
        return { success: false, error: saveError };
      } finally {
        setSaving(false);
      }
    },
    [storageApi],
  );

  /** Update specific fields */
  const updateProfile = useCallback(
    async (updates: Partial<UserProfile>): Promise<ProfileOperationResult> => {
      const updatedProfile: UserProfile = {
        ...profile,
        ...updates,
      };
      return saveProfile(updatedProfile);
    },
    [profile, saveProfile],
  );

  /** Complete the setup wizard */
  const completeSetup = useCallback(
    async (
      wizardProfile: Partial<UserProfile>,
    ): Promise<ProfileOperationResult> => {
      const finalProfile: UserProfile = {
        ...DEFAULT_USER_PROFILE,
        ...wizardProfile,
        version: 1,
        setupCompleted: true,
        createdAt: profile.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      return saveProfile(finalProfile);
    },
    [profile.createdAt, saveProfile],
  );

  /** Reset profile to defaults */
  const resetProfile =
    useCallback(async (): Promise<ProfileOperationResult> => {
      setError(null);
      try {
        await storageApi.remove(STORAGE_KEY);
        setProfile(DEFAULT_USER_PROFILE);
        return { success: true };
      } catch (err) {
        const resetError =
          err instanceof Error ? err : new Error('Failed to reset profile');
        setError(resetError);
        return { success: false, error: resetError };
      }
    }, [storageApi]);

  /** Check if user is new (hasn't completed setup) */
  const isNewUser = useMemo(
    () => !profile.setupCompleted,
    [profile.setupCompleted],
  );

  return {
    profile,
    loading,
    saving,
    error,
    isNewUser,
    saveProfile,
    updateProfile,
    completeSetup,
    resetProfile,
    clearError,
  };
}

export type UseUserProfileReturn = ReturnType<typeof useUserProfile>;
